import { format, isAfter, subHours } from "date-fns";
import { FoldVertical, History, UnfoldVertical } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  AuditActionType,
  type EntityHistoryItem,
  entityHistoryKeys,
  useGetEntityHistory,
} from "@/api/admin/auditLog";
import { CustomButton, Loader, TablePagination } from "@/components/atoms";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { commonIconProps12, commonIconProps20 } from "@/utils/constants";

import { HistoryCard } from "./HistoryCard";
import {
  type FieldMapping,
  type FieldResolver,
  getChanges,
} from "./utils/getChanges";
import type { HistoryTitleResolver } from "./utils/getHistoryTitle";
import { getMediaUrlFieldResolvers } from "./utils/mediaUrlResolver";

const DEFAULT_PAGE_SIZE = 25;
const FIRST_PAGE_PARAMS = { offset: 0, limit: DEFAULT_PAGE_SIZE };

interface EntityHistoryProps {
  entityId?: string;
  entityName: string;
  blockName?: string;
  wrapperClass?: string;
  mapKeyToName?: FieldMapping;
  fieldResolvers?: Record<string, FieldResolver>;
  entityType?: string;
  includeEntityNameInTitle?: boolean;
  resolveHistoryTitle?: HistoryTitleResolver;
  /** When returns true, the log entry is hidden from the history list */
  excludeLog?: (item: EntityHistoryItem) => boolean;
}

const EntityHistory = ({
  entityId,
  entityName,
  wrapperClass,
  blockName = "Details",
  mapKeyToName,
  fieldResolvers,
  entityType,
  includeEntityNameInTitle,
  resolveHistoryTitle,
  excludeLog,
}: EntityHistoryProps) => {
  const shouldIncludeEntityNameInTitle =
    includeEntityNameInTitle ?? !!entityType;
  const [isOpen, setIsOpen] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [tableParams, setTableParams] = useState({
    offset: 0,
    limit: DEFAULT_PAGE_SIZE,
  });

  const mergedFieldResolvers = useMemo(
    () => ({
      ...getMediaUrlFieldResolvers(mapKeyToName),
      ...fieldResolvers,
    }),
    [mapKeyToName, fieldResolvers]
  );

  const filterItems = (items: EntityHistoryItem[]) =>
    items.filter(item => {
      if (excludeLog?.(item)) {
        return false;
      }

      if (!mapKeyToName || item.action !== AuditActionType.UPDATE) {
        return true;
      }

      return getChanges(item, mapKeyToName, mergedFieldResolvers).length > 0;
    });

  const listParams = isOpen ? tableParams : FIRST_PAGE_PARAMS;

  const { data, isLoading } = useGetEntityHistory({
    entityId,
    entityType,
    params: listParams,
  });

  const { data: firstPageData } = useGetEntityHistory({
    entityId,
    entityType,
    params: FIRST_PAGE_PARAMS,
    options: {
      queryKey: entityHistoryKeys.query(
        entityId ?? entityType,
        FIRST_PAGE_PARAMS
      ),
      enabled: isOpen && tableParams.offset > 0,
    },
  });

  useEffect(() => {
    setTableParams(FIRST_PAGE_PARAMS);
  }, [entityId]);

  const handleNext = () => {
    setTableParams(prev => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const handlePrev = () => {
    setTableParams(prev => ({
      ...prev,
      offset: Math.max(prev.offset - prev.limit, 0),
    }));
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (!open) {
      setExpandAll(false);
      setTableParams(FIRST_PAGE_PARAMS);
    }
  };

  const items = filterItems(data?.items ?? []);
  const latestLog = filterItems(
    isOpen && tableParams.offset > 0
      ? (firstPageData?.items ?? [])
      : (data?.items ?? [])
  )[0];

  const hasNewChanges =
    !!latestLog &&
    isAfter(new Date(latestLog.timestamp), subHours(new Date(), 24));

  const groupedLogs = items.reduce(
    (acc, item) => {
      const dateKey = format(
        new Date(item.timestamp),
        "MMMM d, yyyy"
      ).toUpperCase();

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(item);

      return acc;
    },
    {} as Record<string, typeof items>
  );

  return (
    <div className={cn(wrapperClass)}>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <CustomButton variant="icon" className="relative cursor-pointer ">
            <History {...commonIconProps20} />
            {hasNewChanges && (
              <span className="absolute top-[7px] right-[7px] h-1.5 w-1.5 rounded-full bg-orange-500" />
            )}
          </CustomButton>
        </SheetTrigger>

        <SheetContent className="!w-[700px] max-w-[700px] p-0 flex flex-col h-full">
          <SheetHeader className="p-6 border-b flex-shrink-0">
            <SheetTitle>Update history</SheetTitle>
            <div className="flex items-center gap-3 justify-between">
              <p className="text-sm text-muted-foreground">
                {entityName} · {blockName}
              </p>
              {items.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setExpandAll(v => !v)}
                  className="flex items-center text-xs hover:bg-transparent cursor-pointer"
                >
                  {expandAll ? (
                    <FoldVertical {...commonIconProps12} />
                  ) : (
                    <UnfoldVertical {...commonIconProps12} />
                  )}
                  {expandAll ? "Collapse all" : "Expand all"}
                </Button>
              )}
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex h-full min-h-[200px] items-center justify-center">
                <Loader />
              </div>
            ) : items.length === 0 ? (
              <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-2 text-center">
                <History className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium text-muted-foreground">
                  No update history yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Changes to {blockName.toLowerCase()} will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedLogs).map(([date, logs]) => (
                  <div key={date}>
                    <h3 className="mb-4 text-xs font-semibold uppercase text-muted-foreground">
                      {date}
                    </h3>

                    <div className="space-y-4">
                      {logs.map(log => (
                        <HistoryCard
                          key={log.id}
                          log={log}
                          forceExpanded={expandAll}
                          mapKeyToName={mapKeyToName}
                          fieldResolvers={mergedFieldResolvers}
                          includeEntityNameInTitle={
                            shouldIncludeEntityNameInTitle
                          }
                          resolveHistoryTitle={resolveHistoryTitle}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(data?.total ?? 0) > 0 && (
            <div className="border-t p-4 flex-shrink-0">
              <TablePagination
                next={handleNext}
                prev={handlePrev}
                limit={tableParams.limit}
                offset={tableParams.offset}
                total={data?.total ?? 0}
                setLimit={limit => setTableParams({ offset: 0, limit })}
                classes="mt-0"
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default EntityHistory;
